import type { TenantBranding } from "@/api/types";

/**
 * The product's own branding (the root tenant), when the page carries none: only on the Vite dev server
 * until the session loads, and in unit tests. Django always sends the real name (settings.PRODUCT_NAME).
 */
export const ROOT_BRANDING: TenantBranding = {
  is_root: true,
  name: "Hume",
  product_name: "Hume",
  logo_url: null,
  primary_color: null,
  palette: null,
  connected: false,
  dev_hint: "",
  sign_in_label: "",
};

const STYLE_ID = "tenant-palette";
const SHELL_DATA_ID = "shell-branding";

/** The branding Django put in the page shell (index.html), so the first paint is already right. */
export function shellBranding(): TenantBranding {
  try {
    const text = document.getElementById(SHELL_DATA_ID)?.textContent;
    const data = text ? (JSON.parse(text) as TenantBranding) : null;
    return data && typeof data.name === "string" ? data : ROOT_BRANDING;
  } catch {
    return ROOT_BRANDING; // the dev server serves the shell without Django's values
  }
}

/** One step more specific than index.css's `:root` / `.dark`, wherever the style lands in <head>. */
export function paletteCss(palette: TenantBranding["palette"]): string {
  if (!palette) return "";
  const block = (selector: string, variables: Record<string, string>) =>
    `${selector}{${Object.entries(variables)
      .map(([name, value]) => `${name}:${value};`)
      .join("")}}`;
  return block("html:root", palette.light) + block("html.dark", palette.dark);
}

/** The product's name (settings.PRODUCT_NAME), whichever host this is. */
export function productName(branding: TenantBranding): string {
  return branding.product_name || (branding.is_root ? branding.name : ROOT_BRANDING.product_name);
}

/** What titles and copy call the site: the tenant's name on its hosts (white label: never "Hume"), else the
 * product's. */
export function siteName(branding: TenantBranding): string {
  return branding.is_root ? productName(branding) : branding.name;
}

/** The site's name for titles: the tenant's on its hosts, else the product's. */
export function brandName(): string {
  return siteName(shellBranding());
}

/** Apply a tenant's colors and tab icon (its logo) to the document (and undo them for the product's own). */
export function applyBranding(branding: TenantBranding, doc: Document = document): void {
  let style = doc.getElementById(STYLE_ID);
  const css = paletteCss(branding.palette);
  if (css) {
    if (!style) {
      style = doc.createElement("style");
      style.id = STYLE_ID;
      doc.head.appendChild(style);
    }
    if (style.textContent !== css) style.textContent = css;
  } else {
    style?.remove();
  }
  const icon = doc.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (icon) {
    const href = branding.logo_url ?? "/favicon.svg";
    if (icon.getAttribute("href") !== href) {
      icon.setAttribute("href", href);
      if (branding.logo_url) icon.removeAttribute("type");
      else icon.setAttribute("type", "image/svg+xml");
    }
  }
}
